import React from 'react';

interface NavigationDockProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onStartGuidedDemo?: () => void;
}

const NAV_ITEMS = [
  { id: 'overview', icon: 'fa-cubes', label: '공간 트윈', short: '트윈' },
  { id: 'digital-twin', icon: 'fa-wave-square', label: '트윈 분석', short: '분석' },
  { id: 'loop-water', icon: 'fa-droplet', label: '열원 계통', short: '열원' },
  { id: 'loop-air', icon: 'fa-wind', label: '공조 계통', short: '공조' },
  { id: 'ai-control', icon: 'fa-sliders', label: 'AI 제어 분석', short: 'AI 분석' },
  { id: 'ai-model', icon: 'fa-brain', label: '정책 튜닝', short: '튜닝' },
  { id: 'reporting', icon: 'fa-file-shield', label: '운영 리포트', short: '리포트' },
];
const MOBILE_NAV_ITEMS = [
  ...NAV_ITEMS,
  { id: 'settings', icon: 'fa-gear', label: '환경 설정', short: '설정' },
];

export const NavigationDock: React.FC<NavigationDockProps> = ({ activeTab, onTabChange, onStartGuidedDemo }) => {
  const [isOnline, setIsOnline] = React.useState(true);

  React.useEffect(() => {
    const checkStatus = async () => {
      try {
        setIsOnline((await fetch('/api/health')).ok);
      } catch {
        setIsOnline(false);
      }
    };
    checkStatus();
    const timer = window.setInterval(checkStatus, 30000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <nav aria-label="주요 업무 화면" className="fixed bottom-0 left-0 right-0 z-[190] border-t border-[#e1e4e7] bg-white/95 px-2 py-2 backdrop-blur-xl md:bottom-auto md:right-auto md:top-0 md:h-screen md:w-24 md:border-r md:border-t-0 md:px-2 md:py-3">
      <div className="hidden h-full flex-col items-center md:flex">
        <button type="button" onClick={() => onTabChange('overview')} className="mb-5 flex w-full flex-col items-center gap-2 rounded-xl py-2 text-[#171a20]" aria-label="AEES 공간 트윈 홈">
          <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-[#171a20] text-white">
            <i className="fa-solid fa-cube text-base" />
            <span className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${isOnline ? 'bg-[#22a06b]' : 'bg-[#b33f51]'}`} />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#5f656d]">AEES</span>
        </button>

        <div className="h-px w-10 bg-[#e5e7e9]" />
        <div className="mt-4 flex w-full flex-1 flex-col items-center gap-1.5">
          {NAV_ITEMS.map(item => {
            const active = activeTab === item.id || (item.id === 'overview' && activeTab.startsWith('floor-'));
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onTabChange(item.id)}
                title={item.label}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                className={`group relative flex min-h-[64px] w-full flex-col items-center justify-center gap-1.5 rounded-xl px-1 transition-all ${active ? 'bg-[#f0f2f3] text-[#171a20]' : 'text-[#747a82] hover:bg-[#f6f7f8] hover:text-[#171a20]'}`}
              >
                {active && <span className="absolute left-0 h-7 w-[3px] rounded-r bg-[#171a20]" />}
                <i className={`fa-solid ${item.icon} text-sm ${active ? 'text-[#08777d]' : ''}`} />
                <span className="text-[10px] font-medium leading-4">{item.label}</span>
              </button>
            );
          })}
        </div>

        {onStartGuidedDemo && (
          <button type="button" onClick={onStartGuidedDemo} title="순차 시연 가이드" aria-label="순차 시연 가이드" className="mb-1 flex h-11 w-full flex-col items-center justify-center gap-1 rounded-xl border border-[#b9d8d4] bg-[#f3faf9] text-[9px] font-semibold text-[#08777d] hover:bg-[#e7f5f3]">
            <i className="fa-solid fa-person-chalkboard text-xs" />시연
          </button>
        )}
        <button type="button" onClick={() => onTabChange('settings')} title="환경 설정" aria-label="환경 설정" className={`flex h-14 w-full flex-col items-center justify-center gap-1 rounded-xl text-[10px] ${activeTab === 'settings' ? 'bg-[#f0f2f3] text-[#171a20]' : 'text-[#747a82] hover:bg-[#f6f7f8] hover:text-[#171a20]'}`}>
          <i className="fa-solid fa-gear text-xs" />설정
        </button>
      </div>

      <div className="grid grid-cols-8 gap-1 md:hidden">
        {MOBILE_NAV_ITEMS.map(item => {
          const active = activeTab === item.id || (item.id === 'overview' && activeTab.startsWith('floor-'));
          return (
            <button key={item.id} type="button" onClick={() => onTabChange(item.id)} aria-label={item.label} className={`flex h-12 flex-col items-center justify-center gap-1 rounded-lg text-[10px] ${active ? 'bg-[#f0f2f3] text-[#171a20]' : 'text-[#747a82]'}`}>
              <i className={`fa-solid ${item.icon} text-xs`} /><span>{item.short}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
